import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';

import * as customPropTypes from '@/custom-prop-types';
import { Button } from '@/design-system/atoms/Button';
import { LazyIconExternalLink } from '@/design-system/atoms/Icons/IconExternalLink/Lazy';
import Typography from '@/design-system/atoms/Typography';

import * as Card from '../components';
import styles from './SearchResultsCardLocation.module.scss';

/**
 * Search Results Card - Location
 * @param {object} props
 * @param {object} props.badge - Badge object
 * @param {string} props.className - Additional class name
 * @param {object} props.directionsCta - Directions call to action
 * @param {object} props.link - Link object
 * @param {object} props.location - Location object
 * @param {string} props.title - Card title
 * @param {object} props.analytics - Analytics object
 * @param {boolean} props.isInternal - Is internal link
 * @returns
 */
const SearchResultsCardLocation = React.forwardRef(
    (
        {
            analytics,
            badge,
            className,
            directionsCta,
            isInternal,
            link,
            location,
            title,
            coveoAnalyticsCallbacks,
            ...props
        },
        ref,
    ) => {
        const {
            addressOverrideMessage,
            building,
            city,
            parent,
            rootParent,
            state,
            streetOne,
            streetTwo,
            zip,
        } = location;
        const hasAddressOverride = Boolean(addressOverrideMessage?.trim());

        const analyticsObj = useMemo(() => {
            return {
                ...analytics,
                componentTitle: badge?.label,
                locationName: title,
                locationAddress: hasAddressOverride
                    ? addressOverrideMessage
                    : [building, streetOne, streetTwo, city, state, zip]
                          .filter((item) => item)
                          .join(','),
            };
        }, [
            analytics,
            badge?.label,
            title,
            hasAddressOverride,
            addressOverrideMessage,
            building,
            streetOne,
            streetTwo,
            city,
            state,
            zip,
        ]);

        const handleClick = useCallback(() => {
            coveoAnalyticsCallbacks?.onClick?.();
        }, [coveoAnalyticsCallbacks]);

        const handleContextMenu = useCallback(() => {
            coveoAnalyticsCallbacks?.onContextMenu?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseDown = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseDown?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseUp = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseUp?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchStart = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchStart?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchEnd = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchEnd?.();
        }, [coveoAnalyticsCallbacks]);

        useEffect(() => {
            coveoAnalyticsCallbacks?.cancelPendingSelect?.();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);
        return (
            <Card.Container className={className} ref={ref} {...props}>
                <Card.Header>
                    {badge?.type && badge?.label && (
                        <Card.Badge type={badge.type} label={badge.label} />
                    )}
                </Card.Header>
                <Card.Content>
                    <Card.Title
                        analytics={analytics ? analyticsObj : undefined}
                        isInternal={isInternal}
                        link={link}
                        title={title}
                        onClick={coveoAnalyticsCallbacks ? handleClick : undefined}
                        onContextMenu={coveoAnalyticsCallbacks ? handleContextMenu : undefined}
                        onMouseDown={coveoAnalyticsCallbacks ? handleMouseDown : undefined}
                        onMouseUp={coveoAnalyticsCallbacks ? handleMouseUp : undefined}
                        onTouchEnd={coveoAnalyticsCallbacks ? handleTouchEnd : undefined}
                        onTouchStart={coveoAnalyticsCallbacks ? handleTouchStart : undefined}
                    />
                    <div className={styles.location}>
                        {(rootParent || parent) && (
                            <Typography variant="t2b" content={`at ${rootParent || parent}`} />
                        )}
                        {hasAddressOverride ? (
                            <Typography
                                variant="t2"
                                content={addressOverrideMessage}
                                className={styles['location__address']}
                            />
                        ) : (
                            <>
                                {building && (
                                    <Typography
                                        variant="t2"
                                        content={building}
                                        className={styles['location__address']}
                                    />
                                )}
                                {streetOne && city && state && zip && (
                                    <>
                                        <Typography
                                            variant="t2"
                                            content={streetOne}
                                            className={styles['location__address']}
                                        />
                                        {streetTwo && (
                                            <Typography
                                                variant="t2"
                                                content={streetTwo}
                                                className={styles['location__address']}
                                            />
                                        )}
                                        <Typography
                                            variant="t2"
                                            content={`${city}, ${state} ${zip}`}
                                            className={styles['location__address']}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    {directionsCta && (
                        <div className={styles['directions']}>
                            <Button
                                buttonStyle={Button.STYLE.TEXT}
                                label="Directions"
                                iconRight
                                iconComponent={LazyIconExternalLink}
                                className={styles['directions__button']}
                                fullWidth={{ md: true }}
                                alignment="left"
                                {...directionsCta}
                                analytics={analytics ? analyticsObj : undefined}
                            />
                        </div>
                    )}
                </Card.Content>
            </Card.Container>
        );
    },
);

SearchResultsCardLocation.defaultProps = {
    badge: {
        type: 'location',
        label: 'Location',
    },
    link: { csr: false },
    location: {},
};

SearchResultsCardLocation.propTypes = {
    badge: PropTypes.shape({
        type: PropTypes.string,
        label: PropTypes.string,
    }),
    className: PropTypes.string,
    directionsCta: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        ariaLabel: PropTypes.string,
    }),
    isInternal: PropTypes.bool,
    link: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        ariaLabel: PropTypes.string,
    }),
    location: PropTypes.shape({
        addressOverrideMessage: PropTypes.string,
        rootParent: PropTypes.string,
        parent: PropTypes.string,
        building: PropTypes.string,
        streetOne: PropTypes.string,
        streetTwo: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
    }),
    title: PropTypes.string,
    analytics: customPropTypes.analyticsPropType,
};

SearchResultsCardLocation.displayName = 'SearchResultsCardLocation';

export default SearchResultsCardLocation;
